import { resolveDownloadErrorMessage } from '../utils/downloadErrorMessage'
export { resolveDownloadErrorMessage } from '../utils/downloadErrorMessage'

const API_BASE = process.env.VUE_APP_BASE_API || ''

const TOKEN_KEY = 'paleo_user_token'

let unauthorizedHandler = null

export class PaleoApiError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'PaleoApiError'
    this.status = status
  }
}

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler
}

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || ''
}

/** 若依式鉴权失败常为 HTTP 200 + body.code=401，不能只认 response.status */
function isAuthFailure(status, data = {}, message = '') {
  const code = data && data.code
  const msg = message || (data && data.msg) || ''
  if (msg.includes('还可尝试') || msg.includes('已锁定')) {
    return false
  }
  return status === 401
    || code === 401
    || msg.includes('请使用前台账号登录')
    || msg.includes('登录已过期')
    || msg.includes('认证失败')
}

function handleAuthFailure() {
  clearToken()
  if (typeof unauthorizedHandler === 'function') {
    unauthorizedHandler()
  }
}

function buildUrl(path, params) {
  const query = new URLSearchParams()
  Object.keys(params || {}).forEach(key => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      query.append(key, params[key])
    }
  })
  const suffix = query.toString()
  return `${API_BASE}${path}${suffix ? `?${suffix}` : ''}`
}

async function request(path, options = {}) {
  const headers = { ...(options.headers || {}) }
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json;charset=utf-8'
  }
  let response
  try {
    response = await fetch(buildUrl(path, options.params), {
      ...options,
      headers,
      body: options.body instanceof FormData ? options.body : options.body && JSON.stringify(options.body)
    })
  } catch (error) {
    throw new PaleoApiError('网络异常，请稍后重试', 0)
  }
  const text = await response.text()
  let data = {}
  if (text) {
    try {
      data = JSON.parse(text)
    } catch (error) {
      data = {}
    }
  }
  if (isAuthFailure(response.status, data)) {
    handleAuthFailure()
    throw new PaleoApiError('登录已过期，请重新登录', 401)
  }
  if (response.status === 429) {
    throw new PaleoApiError(data.msg || '操作过于频繁，请稍后再试', 429)
  }
  if (response.status >= 500) {
    throw new PaleoApiError(data.msg || '系统繁忙，请稍后重试', response.status)
  }
  if (!response.ok || (data.code && data.code !== 200)) {
    const message = data.msg || '接口请求失败'
    if (isAuthFailure(response.status, data, message)) {
      handleAuthFailure()
      throw new PaleoApiError('登录已过期，请重新登录', 401)
    }
    throw new PaleoApiError(message, response.status)
  }
  return data
}

/**
 * 服务端未按 pageSize 截断时（一次返回整表），按当前页本地切片。
 * 服务端已分页时 rows.length <= pageSize，原样返回。
 */
export function applyClientPageFallback(rows, total, pageNum, pageSize) {
  const list = Array.isArray(rows) ? rows : []
  const size = Number(pageSize) > 0 ? Number(pageSize) : 5
  const page = Number(pageNum) > 0 ? Number(pageNum) : 1
  const counted = Number(total)
  const safeTotal = Number.isFinite(counted) && counted >= 0 ? counted : list.length
  if (list.length <= size) {
    return { rows: list, total: safeTotal }
  }
  const start = (page - 1) * size
  return {
    rows: list.slice(start, start + size),
    total: Math.max(safeTotal, list.length)
  }
}

/**
 * 公开分页列表统一解包。后端 TableDataInfo: { rows, total }。
 * 兼容过渡期旧 AjaxResult.data 数组（仅 total 缺失时用 length）。
 * 传入 paging 时，若服务端一次返回超过 pageSize，则按当前页本地切开。
 */
export function unwrapPublicPage(result, paging) {
  let rows = []
  let total = 0
  if (!result || typeof result !== 'object') {
    rows = []
    total = 0
  } else if (Array.isArray(result.rows)) {
    const parsed = Number(result.total)
    rows = result.rows
    total = Number.isFinite(parsed) ? parsed : result.rows.length
  } else if (Array.isArray(result.data)) {
    rows = result.data
    total = result.data.length
  }
  if (paging && Number(paging.pageSize) > 0) {
    return applyClientPageFallback(rows, total, paging.pageNum, paging.pageSize)
  }
  return { rows, total }
}

export function listBindableAssociations() {
  return request('/paleo/user-bindings/associations')
}

export function listPublicAssociations(params) {
  return request('/paleo/associations/public/list', { params })
}

export function listPublicContents(params) {
  return request('/paleo/contents/public/list', { params })
}

export function getPublicContent(contentId) {
  return request(`/paleo/contents/public/${contentId}`)
}

export function listPublicFiles(params) {
  return request('/paleo/public-files/public/list', { params })
}

export function getPublicFileDownloadInfo(fileId) {
  return request(`/paleo/public-files/${fileId}/download-info`)
}

export function listPublicCmsEntries(params) {
  return request('/paleo/cms/public/list', { params })
}

export function getPublicCmsEntry(entryId) {
  return request(`/paleo/cms/public/${entryId}`)
}

export function submitPartyReport(data) {
  return request('/paleo/party-reports', { method: 'POST', body: data })
}

export function listPublicChannels(params) {
  return request('/paleo/cms-channels/public/list', { params })
}

export function getPublicChannel(params) {
  return request('/paleo/cms-channels/public/detail', { params })
}

export function listOpenConferences(params) {
  return request('/paleo/conferences/public/all', { params })
}

export function issueSlideCaptcha() {
  return request('/captcha/slide')
}

export function verifySlideCaptcha(body) {
  return request('/captcha/slide/verify', { method: 'POST', body })
}

export function login(data) {
  return request('/paleo/auth/login', {
    method: 'POST',
    body: {
      email: data.email || data.account,
      password: data.password,
      slideTicket: data.slideTicket
    }
  })
}

export function startCstCloudLogin(redirectPath) {
  return request('/paleo/auth/cstcloud/login/start', {
    method: 'POST',
    body: { redirectPath }
  })
}

export function consumeCstCloudLogin(ticket) {
  return request('/paleo/auth/cstcloud/login/consume', {
    method: 'POST',
    body: { ticket }
  })
}

export function registerCstCloudUser(data) {
  return request('/paleo/auth/cstcloud/register', { method: 'POST', body: data })
}

export function bindExistingCstCloudUser(data) {
  return request('/paleo/auth/cstcloud/bind-existing', { method: 'POST', body: data })
}

export function getCstCloudBinding() {
  return request('/paleo/auth/cstcloud/binding')
}

export function startCstCloudBinding(redirectPath) {
  return request('/paleo/auth/cstcloud/binding/start', {
    method: 'POST',
    body: { redirectPath }
  })
}

export function reauthorizeCstCloudBinding(redirectPath) {
  return request('/paleo/auth/cstcloud/binding/reauthorize', {
    method: 'POST',
    body: { redirectPath }
  })
}

export function unbindCstCloudBinding(data) {
  return request('/paleo/auth/cstcloud/binding', { method: 'DELETE', body: data })
}

export function listMyLoginRecords(params) {
  return request('/paleo/auth/login-records/mine', { params })
}

export function sendEmailCode(email, bizType = 'REGISTER') {
  return request('/paleo/auth/verification-code/send', { method: 'POST', body: { email, bizType } })
}

export function register(data) {
  return request('/paleo/auth/register', {
    method: 'POST',
    body: {
      email: data.email || data.account,
      verificationCode: data.verificationCode || data.emailOtp,
      password: data.password,
      confirmPassword: data.confirmPassword,
      name: data.name,
      gender: data.gender,
      unit: data.unit,
      role: data.role,
      title: data.title,
      orgCreditCode: data.orgCreditCode,
      slideTicket: data.slideTicket
    }
  })
}

export function logoutApi() {
  return request('/paleo/auth/logout', { method: 'POST' })
}

export function forgotPassword(data) {
  return request('/paleo/auth/password/forgot', { method: 'POST', body: data })
}

export function changePassword(data) {
  return request('/paleo/auth/password', { method: 'PUT', body: data })
}

export function cancelAccount(data) {
  return request('/paleo/auth/account', { method: 'DELETE', body: data })
}

export function updateUserType(userType) {
  return request('/paleo/auth/user-type', { method: 'PUT', body: { userType, membershipChoiceMade: true } })
}

export function requestOrganizationMembership(data) {
  return request('/paleo/membership/organization-binding/request', { method: 'POST', body: data })
}

export function getMembershipStatus() {
  return request('/paleo/membership/status')
}

export function getMembershipEligibility() {
  return request('/paleo/membership/eligibility')
}

export function getConferenceFeeQuote(conferenceId) {
  return request('/paleo/membership/conference-fee-quote', { params: { conferenceId } })
}

export function saveToken(token) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.removeItem('Admin-Token')
  localStorage.removeItem('token')
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem('Admin-Token')
  localStorage.removeItem('token')
}

export function listMyBindings() {
  return request('/paleo/user-bindings/mine')
}

export function bindBranch(branchCode) {
  return request('/paleo/user-bindings/mine/bind', { method: 'POST', body: { branchCode } })
}

export function unbindBranch(branchCode) {
  return request('/paleo/user-bindings/mine/unbind', { method: 'POST', body: { branchCode } })
}

export function bindAssociation(associationId) {
  return request(`/paleo/user-bindings/${associationId}`, { method: 'POST' })
}

export function unbindAssociation(associationId) {
  return request(`/paleo/user-bindings/${associationId}`, { method: 'DELETE' })
}

export function listPublicConferences(params) {
  return request('/paleo/conferences/public/list', { params })
}

export function listMyContents(params) {
  return request('/paleo/contents/mine', { params })
}

export function getConferenceMaterials(conferenceId) {
  return request(`/paleo/conferences/${conferenceId}/materials`)
}

export function listMyNotifications(params) {
  return request('/paleo/notifications/mine', { params })
}

export function markMyNotificationsRead(notificationIds) {
  return request('/paleo/notifications/mine/read', { method: 'POST', body: notificationIds })
}

/**
 * 剥掉误拼的 API 前缀，保证 /paleo/files/secure 与 fetch 使用业务相对路径。
 */
function toRelativeFilePath(fileUrl) {
  let path = String(fileUrl || '').trim()
  if (!path) {
    return ''
  }
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname || ''
    } catch (e) {
      return ''
    }
  }
  if (!path.startsWith('/')) {
    path = `/${path}`
  }
  const prefix = String(API_BASE || '').replace(/\/$/, '')
  if (prefix && !/^https?:\/\//i.test(prefix)) {
    while (path === prefix || path.startsWith(`${prefix}/`)) {
      path = path === prefix ? '/' : path.slice(prefix.length)
    }
  }
  return path.replace(/[?#].*$/, '')
}

function throwFileAccessError(response, data = {}, { clearSessionOnAuthFailure = true } = {}) {
  const status = (data && data.code) || response.status || 500
  const rawMessage = (data && data.msg) || '文件下载失败'
  // 业务无权（403）绝不能清登录态，否则会出现「下载失败 → 假过期 → 登录死循环」
  if (status === 403 || (data && data.code === 403)) {
    throw new PaleoApiError(rawMessage || '无权下载该文件', 403)
  }
  if (isAuthFailure(status, data, rawMessage)) {
    if (clearSessionOnAuthFailure) {
      handleAuthFailure()
      throw new PaleoApiError('登录已过期，请重新登录', 401)
    }
    throw new PaleoApiError(rawMessage.includes('登录') ? rawMessage : '请登录后下载', 401)
  }
  const mapped = resolveDownloadErrorMessage({ status, message: rawMessage })
  throw new PaleoApiError(mapped, status)
}

/**
 * 从 Content-Disposition 解析下载文件名。
 */
function fileNameFromContentDisposition(header) {
  const value = String(header || '')
  if (!value) {
    return ''
  }
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(value)
  if (utf8 && utf8[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^["']|["']$/g, ''))
    } catch (e) {
      return utf8[1].trim().replace(/^["']|["']$/g, '')
    }
  }
  const plain = /filename=([^;]+)/i.exec(value)
  if (plain && plain[1]) {
    return plain[1].trim().replace(/^["']|["']$/g, '')
  }
  return ''
}

/**
 * GET /paleo/files/secure — 会议盖章通知、摘要模板、凭证/发票等业务文件。
 * 返回 { objectUrl, fileName }，调用方负责 revoke objectUrl。
 */
export async function fetchSecureFileBlob(fileUrl, options = {}) {
  const path = toRelativeFilePath(fileUrl)
  if (!path) {
    throw new PaleoApiError('文件地址为空', 400)
  }
  const clearSessionOnAuthFailure = options.clearSessionOnAuthFailure !== false
  const token = getToken()
  let response
  try {
    response = await fetch(buildUrl('/paleo/files/secure', { url: path }), {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
  } catch (error) {
    throw new PaleoApiError(resolveDownloadErrorMessage({ status: 0, message: (error && error.message) || '网络异常' }), 0)
  }
  const contentType = (response.headers.get('content-type') || '').toLowerCase()
  if (!response.ok || contentType.includes('application/json')) {
    let data = {}
    try {
      data = await response.json()
    } catch (error) {
      data = {}
    }
    throwFileAccessError(response, data, { clearSessionOnAuthFailure })
  }
  const headerName = fileNameFromContentDisposition(response.headers.get('content-disposition'))
  const objectUrl = URL.createObjectURL(await response.blob())
  return {
    objectUrl,
    fileName: headerName || options.fileName || ''
  }
}

/** @deprecated 使用 fetchSecureFileBlob；保留兼容预览场景 */
export async function fetchSecureFileObjectUrl(fileUrl, options = {}) {
  const result = await fetchSecureFileBlob(fileUrl, options)
  return result.objectUrl
}

/** 通过 /paleo/files/secure 触发浏览器下载；优先用响应头原文件名。 */
export async function downloadSecureFile(fileUrl, fileName = '') {
  const { objectUrl, fileName: serverName } = await fetchSecureFileBlob(fileUrl, {
    clearSessionOnAuthFailure: true,
    fileName
  })
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = serverName || fileName || 'download'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30000)
}

/**
 * 直链鉴权下载（兼容旧调用）。会议资料请优先 downloadSecureFile。
 * Opening /storage/files directly cannot carry JWT in a new tab.
 */
export async function downloadStorageFile(fileUrl, fileName = '') {
  if (!fileUrl) {
    throw new PaleoApiError('文件地址为空', 400)
  }
  const relative = toRelativeFilePath(fileUrl)
  const target = /^https?:\/\//i.test(fileUrl) && !relative
    ? fileUrl
    : `${API_BASE}${relative || (fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`)}`
  const token = getToken()
  let response
  try {
    response = await fetch(target, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
  } catch (error) {
    throw new PaleoApiError(resolveDownloadErrorMessage({ status: 0, message: (error && error.message) || '网络异常' }), 0)
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase()
  if (!response.ok || contentType.includes('application/json')) {
    let data = {}
    try {
      data = await response.json()
    } catch (error) {
      data = {}
    }
    throwFileAccessError(response, data, { clearSessionOnAuthFailure: true })
  }

  const objectUrl = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName || 'download'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

export function listMyRegistrations(params) {
  return request('/paleo/registrations/mine', { params })
}

export function addMyRegistration(data) {
  return request('/paleo/registrations/mine', { method: 'POST', body: data })
}

export function updateMyParticipant(registrationId, data) {
  return request(`/paleo/registrations/mine/${registrationId}/participant`, { method: 'POST', body: data })
}

export function getMyAttendee(registrationId) {
  return request(`/paleo/registrations/mine/${registrationId}/attendee`)
}

export function deleteMyAbstract(registrationId) {
  return request(`/paleo/registrations/mine/${registrationId}/abstract`, { method: 'DELETE' })
}

export function getConferenceByCode(conferenceCode) {
  return request(`/paleo/conferences/public/${conferenceCode}`)
}

export function uploadMyRegistrationFile(registrationId, fileRole, file) {
  const formData = new FormData()
  formData.append('file', file)
  return request(`/paleo/registrations/mine/${registrationId}/files/${fileRole}`, { method: 'POST', body: formData })
}

export function listMyApplications(params) {
  return request('/paleo/membership/applications/mine', { params })
}

export function getMyMemberProfile() {
  return request('/paleo/membership/profiles/mine')
}

export function updateMyMemberProfile(data) {
  return request('/paleo/membership/profiles/mine', { method: 'PUT', body: data })
}

export function getUserProfile() {
  return request('/paleo/auth/info')
}

export function updateUserProfile(data) {
  return request('/paleo/auth/profile', {
    method: 'PUT',
    body: {
      userName: data.userName || data.nickName,
      gender: data.gender,
      unit: data.unit,
      role: data.role || data.roleLabel,
      title: data.title,
      orgCreditCode: data.orgCreditCode != null ? data.orgCreditCode : ''
    }
  })
}

export function addMyApplication(data) {
  return request('/paleo/membership/applications/mine', { method: 'POST', body: data })
}

export function cancelMyApplication(applicationId) {
  return request(`/paleo/membership/applications/mine/${applicationId}/cancel`, { method: 'POST' })
}

export function getPublicMembershipTemplates() {
  return request('/paleo/membership/templates/public')
}

export function getMembershipBankAccount() {
  return request('/paleo/membership/bank-account/public')
}

export function getMembershipFeeConfig() {
  return request('/paleo/membership/fee-config/public')
}

export function uploadMyApplicationFile(applicationId, file) {
  const formData = new FormData()
  formData.append('file', file)
  return request(`/paleo/membership/applications/mine/${applicationId}/file`, { method: 'POST', body: formData })
}

export function listMembershipTemplates(params) {
  return request('/paleo/membership/templates/list', { params })
}

export function listMyPayments(params) {
  return request('/paleo/membership/payments/mine', { params })
}

export function addMyPayment(data) {
  return request('/paleo/membership/payments/mine', { method: 'POST', body: data })
}

export function cancelMyPayment(paymentId) {
  return request(`/paleo/membership/payments/mine/${paymentId}/cancel`, { method: 'POST' })
}

export function uploadMyPaymentFile(paymentId, fileRole, file) {
  const formData = new FormData()
  formData.append('file', file)
  return request(`/paleo/membership/payments/mine/${paymentId}/files/${fileRole}`, { method: 'POST', body: formData })
}

// ── 电子会员证 ──

export function getMyMemberCertificate() {
  return request('/paleo/member-cert/mine')
}

export function uploadMemberCertPhoto(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request('/paleo/member-cert/photo', { method: 'POST', body: formData })
}

/** 下载电子证 PDF（blob）；失败时抛出 PaleoApiError */
export async function downloadMemberCertificate() {
  const headers = {}
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  let response
  try {
    response = await fetch(buildUrl('/paleo/member-cert/download'), { method: 'GET', headers })
  } catch (error) {
    throw new PaleoApiError('网络异常，请稍后重试', 0)
  }
  const contentType = (response.headers.get('content-type') || '').toLowerCase()
  if (contentType.includes('application/json')) {
    let data = {}
    try {
      data = await response.json()
    } catch (e) {
      data = {}
    }
    if (isAuthFailure(response.status, data)) {
      handleAuthFailure()
      throw new PaleoApiError('登录已过期，请重新登录', 401)
    }
    throw new PaleoApiError((data && data.msg) || '下载失败', response.status || (data && data.code) || 500)
  }
  if (isAuthFailure(response.status)) {
    handleAuthFailure()
    throw new PaleoApiError('登录已过期，请重新登录', 401)
  }
  if (!response.ok) {
    throw new PaleoApiError('下载失败', response.status)
  }
  return response.blob()
}
